/**
 * Package addons Alpine.js component
 * Handles validation and interaction for package addon checkboxes
 */
export default function PackageAddons() {
  return {
    /**
     * Component state
     */
    initialized: false,
    hasInteracted: false,
    config: {},

    /**
     * Initialize the component
     */
    init() {
      this.initialized = false;
      this.config = this.parseConfig();
      this.hasInteracted = false;

      // Initial validation check
      this.$nextTick(() => {
        this.initialized = true;
        const requirementType = this.config.requirementType || 'optional';

        // If not optional, set initial state to invalid
        if (requirementType !== 'optional') {
          this.dispatchValidationEvent(false);
        }

        this.validateAddons();
      });
    },

    /**
     * Parse the config from data attribute
     */
    parseConfig() {
      try {
        return JSON.parse(this.$el.dataset.packageAddonsConfig || '{}');
      } catch (error) {
        console.error('Failed to parse package addons config:', error);
        return {
          validationMessages: {},
          requirementType: 'optional',
          initialized: false,
        };
      }
    },

    /**
     * Get parent store from Alpine.js
     */
    getParentStore() {
      try {
        const parentEl = this.$el.closest('[x-data]');
        if (!parentEl) return null;
        return window.Alpine.$data(parentEl)?.store;
      } catch (error) {
        console.error('Failed to get parent store:', error);
        return null;
      }
    },

    /**
     * Dispatch validation event
     */
    dispatchValidationEvent(isValid) {
      const wrapper = this.$el.closest('[x-ref="wrapper"]');
      if (wrapper) {
        wrapper.dispatchEvent(
          new CustomEvent('addons-validation-changed', {
            detail: { isValid },
            bubbles: true,
          }),
        );
      }
    },

    /**
     * Dispatch update event
     */
    dispatchUpdateEvent(params, selectedAddons, isValid) {
      const wrapper = this.$el.closest('[x-ref="wrapper"]');
      if (wrapper) {
        wrapper.dispatchEvent(
          new CustomEvent('package-addons-updated', {
            detail: {
              type: 'addon',
              params,
              addons: selectedAddons,
              isValid,
            },
            bubbles: true,
          }),
        );
      }
    },

    /**
     * Validate addons based on requirement type
     */
    validateAddons($event = null) {
      if (!this.initialized) {
        return;
      }

      const parentStore = this.getParentStore();
      if (!parentStore) {
        return;
      }

      // Mark user interaction
      if ($event) {
        this.hasInteracted = true;
      }

      const addonsContainer = this.$refs.addonsContainer;
      if (!addonsContainer) {
        return;
      }

      const allAddons = Array.from(addonsContainer.querySelectorAll('.js-package-addon'));
      const checkedAddons = Array.from(addonsContainer.querySelectorAll('.js-package-addon:checked'));
      const requiredAddons = allAddons.filter((addon) => addon.dataset.required === 'true');

      // Validate based on requirement type
      let isValid = true;
      let errors = [];

      // First check required addons
      if (requiredAddons.length > 0) {
        isValid = requiredAddons.every((addon) => addon.checked);
      }

      // Additional validation based on requirement type
      const requirementType = this.config.requirementType || 'optional';
      switch (requirementType) {
        case 'all_required':
          isValid = checkedAddons.length === allAddons.length;
          if (!isValid) {
            errors.push(this.config.validationMessages?.all_required);
          }
          break;
        case 'at_least_one_required':
          isValid = checkedAddons.length >= 1;
          if (!isValid) {
            errors.push(this.config.validationMessages?.at_least_one);
          }
          break;
        case 'individual':
          if (!isValid) {
            errors.push(this.config.validationMessages?.required_field);
          }
          break;
      }

      // Don't double-prefix types that are already `addon_*`.
      const toParamKey = (type) => (type.startsWith('addon_') ? type : `addon_${type}`);

      // Update selected addons
      const selectedAddons = checkedAddons.map((addon) => ({
        type: addon.value,
        param: toParamKey(addon.value),
        value: true,
      }));
      parentStore.selectedAddons = selectedAddons;

      // Create params object if we have an event
      const params = $event
        ? {
            [toParamKey($event.target.value)]: $event.target.checked,
          }
        : {};

      // Update store errors if user has interacted
      if (this.hasInteracted) {
        parentStore.errors = errors.length > 0 ? { addons: errors } : {};
      }

      // Dispatch events
      this.dispatchValidationEvent(isValid);
      this.dispatchUpdateEvent(params, selectedAddons, isValid);
    },
  };
}
